import React from 'react';
import { Button, Tooltip } from 'antd';
import {
  ReloadOutlined, BulbOutlined, CheckCircleFilled,
  WarningFilled, CloseCircleFilled, InfoCircleOutlined,
} from '@ant-design/icons';

const COLORS = {
  primary: '#1a1a2e',
  surface: '#1e1e3a',
  surfaceLight: '#252547',
  border: 'rgba(255,255,255,0.08)',
  text: '#e2e8f0',
  textSecondary: '#94a3b8',
  textMuted: '#64748b',
  emerald: '#10b981',
  emeraldBg: 'rgba(16,185,129,0.12)',
  amber: '#f59e0b',
  amberBg: 'rgba(245,158,11,0.12)',
  rose: '#ef4444',
  roseBg: 'rgba(239,68,68,0.12)',
  violet: '#8b5cf6',
  violetBg: 'rgba(139,92,246,0.12)',
  blue: '#3b82f6',
  blueBg: 'rgba(59,130,246,0.12)',
  orange: '#f97316',
  orangeBg: 'rgba(249,115,22,0.12)',
  glass: 'rgba(255,255,255,0.04)',
  glassBorder: 'rgba(255,255,255,0.08)',
};

const GRADE_CONFIG = {
  A: { color: COLORS.emerald, bg: COLORS.emeraldBg, label: 'Excellent', stroke: COLORS.emerald },
  B: { color: COLORS.blue, bg: COLORS.blueBg, label: 'Good', stroke: COLORS.blue },
  C: { color: COLORS.amber, bg: COLORS.amberBg, label: 'Fair', stroke: COLORS.amber },
  D: { color: COLORS.orange, bg: COLORS.orangeBg, label: 'Poor', stroke: COLORS.orange },
  F: { color: COLORS.rose, bg: COLORS.roseBg, label: 'Critical', stroke: COLORS.rose },
};

const glassCard = {
  background: COLORS.glass,
  backdropFilter: 'blur(16px)',
  WebkitBackdropFilter: 'blur(16px)',
  border: `1px solid ${COLORS.glassBorder}`,
  borderRadius: 12,
};

const SpamScoreCard = ({ score = 0, grade = 'C', details = [], recommendations = [], onRefresh }) => {
  const config = GRADE_CONFIG[grade] || GRADE_CONFIG.C;
  const normalizedScore = Math.max(0, Math.min(100, score));
  const circumference = 2 * Math.PI * 54;
  const strokeDashoffset = circumference - (normalizedScore / 100) * circumference;

  return (
    <div style={{ ...glassCard, padding: 20, display: 'flex', flexDirection: 'column', gap: 20 }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div>
          <div style={{ fontSize: 15, fontWeight: 700, color: COLORS.text }}>Spam Score</div>
          <div style={{ fontSize: 12, color: COLORS.textSecondary }}>{config.label}</div>
        </div>
        <Button
          type="text"
          icon={<ReloadOutlined />}
          onClick={onRefresh}
          style={{ color: COLORS.violet }}
        >
          Refresh
        </Button>
      </div>

      {/* Score Gauge */}
      <div style={{ display: 'flex', justifyContent: 'center', position: 'relative' }}>
        <svg width="140" height="140" viewBox="0 0 120 120">
          {/* Background circle */}
          <circle
            cx="60"
            cy="60"
            r="54"
            fill="none"
            stroke={COLORS.border}
            strokeWidth="8"
          />
          {/* Score arc */}
          <circle
            cx="60"
            cy="60"
            r="54"
            fill="none"
            stroke={config.stroke}
            strokeWidth="8"
            strokeLinecap="round"
            strokeDasharray={circumference}
            strokeDashoffset={strokeDashoffset}
            transform="rotate(-90 60 60)"
            style={{ transition: 'stroke-dashoffset 0.8s ease' }}
          />
          {/* Glow effect */}
          <circle
            cx="60"
            cy="60"
            r="54"
            fill="none"
            stroke={config.stroke}
            strokeWidth="2"
            strokeLinecap="round"
            strokeDasharray={circumference}
            strokeDashoffset={strokeDashoffset}
            transform="rotate(-90 60 60)"
            opacity="0.3"
            filter="blur(4px)"
            style={{ transition: 'stroke-dashoffset 0.8s ease' }}
          />
        </svg>
        {/* Grade Letter Center */}
        <div style={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}>
          <span style={{
            fontSize: 36,
            fontWeight: 800,
            color: config.color,
            lineHeight: 1,
          }}>
            {grade}
          </span>
          <span style={{ fontSize: 14, fontWeight: 600, color: COLORS.textSecondary }}>
            {normalizedScore}/100
          </span>
        </div>
      </div>

      {/* Details List */}
      {details.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          <div style={{
            fontSize: 11, fontWeight: 600, color: COLORS.textSecondary,
            textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 4,
          }}>
            Score Breakdown
          </div>
          {details.map((detail, idx) => {
            const isGood = (detail.points || 0) >= 0;
            return (
              <div
                key={idx}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  padding: '6px 10px',
                  borderRadius: 8,
                  background: isGood ? COLORS.emeraldBg : COLORS.roseBg,
                }}
              >
                {isGood ? (
                  <CheckCircleFilled style={{ color: COLORS.emerald, fontSize: 12 }} />
                ) : (
                  <CloseCircleFilled style={{ color: COLORS.rose, fontSize: 12 }} />
                )}
                <span style={{ flex: 1, fontSize: 12, color: COLORS.text }}>
                  {detail.rule || detail.description || 'Rule check'}
                </span>
                <Tooltip title={detail.description || ''}>
                  <InfoCircleOutlined style={{ color: COLORS.textMuted, fontSize: 11, cursor: 'help' }} />
                </Tooltip>
                <span style={{
                  fontSize: 11, fontWeight: 700,
                  color: isGood ? COLORS.emerald : COLORS.rose,
                  minWidth: 30, textAlign: 'right',
                }}>
                  {isGood ? '+' : ''}{detail.points || 0}
                </span>
              </div>
            );
          })}
        </div>
      )}

      {/* Recommendations */}
      {recommendations.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          <div style={{
            fontSize: 11, fontWeight: 600, color: COLORS.textSecondary,
            textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 4,
          }}>
            Recommendations
          </div>
          {recommendations.map((rec, idx) => (
            <div
              key={idx}
              style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: 8,
                padding: '8px 10px',
                borderRadius: 8,
                background: COLORS.amberBg,
              }}
            >
              <BulbOutlined style={{ color: COLORS.amber, fontSize: 14, marginTop: 1 }} />
              <span style={{ fontSize: 12, color: COLORS.text, lineHeight: 1.4 }}>
                {typeof rec === 'string' ? rec : rec.text || rec.message || JSON.stringify(rec)}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default SpamScoreCard;
